import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { addDays, startOfDay } from 'date-fns';
import { normalize, schema } from 'normalizr';
import { combineReducers } from 'redux';
import { createTableSlice } from '../../../common/datagrid';
import {
    PriceChangeDirection,
    PriceDto,
    TableType,
    UpdatePriceBasedOnDateDto
} from '../../../common/PricePrgClient';
import { createAsyncAction, deletePropertiesStartingWith, distinct, toDateString } from '../../../utils';
import { fetchPricelistPeriodsForPricelists } from '../../customer/redux/customerSlices';
import { fetchProducts } from '../../product/redux/productSlices';
import { fetchUserSettings } from '../../settings/redux/settingsSlices';
import { fetchWarehouses } from '../../stock/redux/stockSlices';
import { Price } from '../priceTypes';

const namespaces = {
    selectedPricelistIds: 'selectedPricelistIds',
    priceSelectedDate: 'priceSelectedDate',
    priceDialogs: 'priceDialogs',
    prices: 'prices',
    isLoading: 'isLoading',
    checkboxFilters: 'checkboxFilters',
    priceWarehouse: 'priceWarehouse',
    useCalculations: 'useCalculations',
    isExporting: 'isExporting',
    rootPricelistPeriodDates: 'rootPricelistPeriodDates'
};

const rootPricelistPeriodDatesSlice = createSlice({
    name: namespaces.rootPricelistPeriodDates,
    initialState: {} as { [pricelistId: number]: string[] },
    reducers: {},
    extraReducers: builder =>
        builder.addCase(fetchPricelistPeriodsForPricelists.success, (state, { payload }) => {
            return payload.reduce((obj, pricelistPeriod) => {
                if (obj[pricelistPeriod['pricelistId']] == null) {
                    obj[pricelistPeriod['pricelistId']] = [];
                }
                return {
                    ...obj,
                    [pricelistPeriod['pricelistId']]: [
                        ...obj[pricelistPeriod['pricelistId']],
                        pricelistPeriod.validFrom
                    ]
                };
            }, {});
        })
});

const selectedPricelistIdsSlice = createSlice({
    name: namespaces.selectedPricelistIds,
    initialState: [] as number[],
    reducers: {
        priceTablePricelistsSelected: (state, action: PayloadAction<number[]>) => action.payload
    },
    extraReducers: builder =>
        builder.addCase(fetchUserSettings.success, (state, { payload }) => {
            const tableSettings = payload?.tableSettings?.find(ts => ts.table === TableType.Price);
            if (tableSettings != null) {
                return tableSettings.tableColumnSettings
                    .filter(tcs => !initialPriceTableColumns.some(iptc => iptc.key === tcs.name))
                    .map(tcs => Number(tcs.name));
            }
            return state;
        })
});
export const { priceTablePricelistsSelected } = selectedPricelistIdsSlice.actions;

const selectedDateSlice = createSlice({
    name: namespaces.priceSelectedDate,
    initialState: toDateString(addDays(startOfDay(new Date()), 2)),
    reducers: {
        pricelistDateSelected: (state, action: PayloadAction<string>) => toDateString(action.payload)
    }
});
export const { pricelistDateSelected } = selectedDateSlice.actions;

const dialogsSlice = createSlice({
    name: namespaces.priceDialogs,
    initialState: {
        isChoosePricelistsDialogOpen: false
    },
    reducers: {
        openChoosePricelistsDialog: state => {
            state.isChoosePricelistsDialogOpen = true;
        },
        closeChoosePricelistsDialog: state => {
            state.isChoosePricelistsDialogOpen = false;
        }
    }
});
export const { closeChoosePricelistsDialog, openChoosePricelistsDialog } = dialogsSlice.actions;

const getPriceKey = (price: PriceDto) => `${price.productId}-${price.pricelistId}`;
const priceEntity = new schema.Entity<PriceDto>('prices', undefined, {
    idAttribute: getPriceKey
});

type FetchPricesRequest = { pricelistIds: number[]; validAt: string };
type FetchPricesPartialRequest = FetchPricesRequest & { productIds: number[] };
type UpdatePriceRequest = UpdatePriceBasedOnDateDto & {
    isAwareOfChangingActualPricelist: boolean;
    refetchPrices?: boolean;
};
type UpdatePriceSuccess = {
    pricelistPeriodCopied: boolean;
    pricelistIdsCopied: number[];
    productId: number;
};
type SavePriceCommentRequest = {
    productId: number;
    pricelistId: number;
    priceId: number;
    comment: string | null;
};
type SavePriceCommentSuccess = { priceIds: number[]; comment: string | null };
type CalculateProductPrices = { productId: number; currentPrice: number; pricelistId: number };
type CancelPriceUpdate = { productId: number; pricelistId: number };
export const updatePrices = createAsyncAction<UpdatePriceRequest, UpdatePriceSuccess>(
    namespaces.prices,
    'updatePrices'
);
export const fetchPrices = createAsyncAction<FetchPricesRequest, PriceDto[]>(
    namespaces.prices,
    'fetchPrices'
);
export const fetchPricesPartial = createAsyncAction<FetchPricesPartialRequest, PriceDto[]>(
    namespaces.prices,
    'fetchPricesPartial'
);
export const savePriceComment = createAsyncAction<SavePriceCommentRequest, SavePriceCommentSuccess>(
    namespaces.prices,
    'savePriceComment'
);

const pricesSlice = createSlice({
    name: namespaces.prices,
    initialState: {} as { [priceKey: string]: Price },
    reducers: {
        calculateProductPrices: (state, action: PayloadAction<CalculateProductPrices>) => {
            const { payload } = action;

            const price = state[`${payload.productId}-${payload.pricelistId}`];
            if (price == null) {
                state[`${payload.productId}-${payload.pricelistId}`] = {
                    price: 0,
                    priceToBeSaved: payload.currentPrice,
                    priceChangeDirection: PriceChangeDirection.NewArticle,
                    pricelistId: payload.pricelistId,
                    productId: payload.productId,
                    agreementPriceIds: [],
                    isActive: true,
                    appliedCalculations: true,
                    exchangeRate: 1
                };
            } else {
                price.priceToBeSaved = payload.currentPrice;
            }
        },
        cancelPriceUpdate: (state, action: PayloadAction<CancelPriceUpdate>) => {
            const { payload } = action;

            const price = state[`${payload.productId}-${payload.pricelistId}`];
            if (price != null) {
                if (price.price === 0) {
                    delete state[`${payload.productId}-${payload.pricelistId}`];
                } else {
                    delete price.priceToBeSaved;
                }
            }
        }
    },
    extraReducers: builder =>
        builder
            .addCase(fetchProducts.success, (state, action) => {
                const oldProductIds = Object.keys(state)
                    .map(x => Number(x.split('-')[0]))
                    .filter(distinct);

                const newProductIds = new Set(action.payload.map(x => x.productId));

                for (const oldProductId of oldProductIds) {
                    if (!newProductIds.has(oldProductId)) {
                        deletePropertiesStartingWith(String(oldProductId) + '-', state);
                    }
                }
            })
            .addCase(updatePrices.success, (state, action) => {
                const { pricelistIdsCopied, pricelistPeriodCopied, productId } = action.payload;

                if (pricelistPeriodCopied) {
                    for (const key in state) {
                        if (Object.prototype.hasOwnProperty.call(state, key)) {
                            const price = state[key];
                            if (
                                price.productId !== productId &&
                                pricelistIdsCopied.includes(price.pricelistId)
                            ) {
                                price.priceChangeDirection = PriceChangeDirection.Unaffected;
                            }
                        }
                    }
                }
            })
            .addCase(fetchPrices.success, (_, action) => {
                const normalized = normalize(action.payload, [priceEntity]);
                return normalized.entities.prices ?? {};
            })
            .addCase(fetchPricesPartial.success, (state, action) => {
                const { payload } = action;
                for (const price of payload) {
                    if (price.price === 0) {
                        delete state[getPriceKey(price)];
                    } else {
                        state[getPriceKey(price)] = price;
                    }
                }
            })
            .addCase(savePriceComment.success, (state, action) => {
                const { priceIds, comment } = action.payload;

                for (const priceId of priceIds) {
                    for (const price of Object.values(state)) {
                        if (price.priceId === priceId) {
                            if (!comment) {
                                delete price.comment;
                            } else {
                                price.comment = comment;
                            }
                        }
                    }
                }
            })
});
export const { calculateProductPrices, cancelPriceUpdate } = pricesSlice.actions;

const isLoadingSlice = createSlice({
    name: namespaces.isLoading,
    initialState: false,
    reducers: {},
    extraReducers: builder =>
        builder.addCase(fetchPrices.request, () => true).addCase(fetchPrices.success, () => false)
});

const initialPriceTableColumns = [
    'productId',
    'productName',
    'customProductAttribute1',
    'netWeight',
    'unit',
    'country',
    'purchaser',
    'stock',
    'boxesPerPallet'
].map(key => ({
    key: key,
    hide: key === 'customProductAttribute1',
    width: undefined
}));

const priceTableSlice = createTableSlice(
    'priceTable',
    {
        table: TableType.Price,
        filters: {
            productId: '',
            productName: '',
            customProductAttribute1: '',
            netWeight: '',
            unit: '',
            country: '',
            purchaser: '',
            stock: '',
            boxesPerPallet: ''
        },
        sorting: {
            column: 'productId',
            direction: 'asc'
        },
        columns: initialPriceTableColumns,
        showTooltips: false
    },
    builder =>
        builder.addCase(priceTablePricelistsSelected, (state, action) => {
            const selectedPricelistIds = action.payload;
            for (const pricelistId of selectedPricelistIds) {
                state.filters[pricelistId] = '';
            }

            state.columns = [
                ...initialPriceTableColumns,
                ...selectedPricelistIds.map(s => ({ key: String(s), hide: false, width: undefined }))
            ];
        })
);
export const { priceTableActions } = priceTableSlice;

const checkboxFiltersSlice = createSlice({
    name: namespaces.checkboxFilters,
    initialState: {
        showAllArticles: false,
        showCommentsOnly: false,
        showNewArticlesOnly: false
    },
    reducers: {
        showAllArticlesChanged: (state, action: PayloadAction<boolean>) => {
            state.showAllArticles = action.payload;
        },
        showCommentsOnlyChanged: (state, action: PayloadAction<boolean>) => {
            state.showCommentsOnly = action.payload;
        },
        showNewArticlesOnlyChanged: (state, action: PayloadAction<boolean>) => {
            state.showNewArticlesOnly = action.payload;
        }
    }
});
export const { showAllArticlesChanged, showCommentsOnlyChanged, showNewArticlesOnlyChanged } =
    checkboxFiltersSlice.actions;

const warehouse = createSlice({
    name: namespaces.priceWarehouse,
    initialState: {
        warehouse: null as number | null,
        date: toDateString(addDays(startOfDay(new Date()), 1))
    },
    reducers: {
        warehouseSelected: (state, action: PayloadAction<number>) => {
            state.warehouse = action.payload;
        },
        warehouseDateSelected: (state, action: PayloadAction<string>) => {
            state.date = toDateString(action.payload);
        }
    },
    extraReducers: builder =>
        builder.addCase(fetchWarehouses.success, (state, { payload }) => {
            if (payload.length === 0) {
                return;
            }

            state.warehouse =
                payload.find(w => w.isPrimaryWarehouse)?.warehouseId ?? payload[0].warehouseId ?? null;
        })
});
export const { warehouseSelected, warehouseDateSelected } = warehouse.actions;

const applyCalculationsSlice = createSlice({
    name: namespaces.useCalculations,
    initialState: true,
    reducers: {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        toggleApplyCalculations: (state, action: PayloadAction<void>) => !state
    }
});
export const { toggleApplyCalculations } = applyCalculationsSlice.actions;

const isExportingSlice = createSlice({
    name: namespaces.isExporting,
    initialState: false,
    reducers: {
        setIsExporting: (state, action: PayloadAction<boolean>) => action.payload
    }
});

export const { setIsExporting } = isExportingSlice.actions;

export default combineReducers({
    prices: pricesSlice.reducer,
    selectedPricelistIds: selectedPricelistIdsSlice.reducer,
    selectedDate: selectedDateSlice.reducer,
    selectedWarehouse: warehouse.reducer,
    rootPricelistPeriodDates: rootPricelistPeriodDatesSlice.reducer,
    ui: combineReducers({
        priceTable: priceTableSlice.reducer,
        checkboxFilters: checkboxFiltersSlice.reducer,
        isLoading: isLoadingSlice.reducer,
        dialogs: dialogsSlice.reducer,
        applyCalculations: applyCalculationsSlice.reducer,
        isExporting: isExportingSlice.reducer
    })
});
